import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Paperclip, X, FileText, Film, Image, Download, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUploadAttachments, useDeleteAttachment } from '../../hooks/useIssues';
import { useAuthStore } from '../../store/authStore';
import Spinner from '../ui/Spinner';
import type { IssueAttachment } from '../../services/issueService';

interface Props {
  projectId: string;
  issueId: string;
  attachments: IssueAttachment[];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <Image className="size-5 text-blue-500" />;
  if (mimeType.startsWith('video/')) return <Film className="size-5 text-purple-500" />;
  if (mimeType === 'application/pdf') return <FileText className="size-5 text-red-500" />;
  return <FileText className="size-5 text-gray-500" />;
}

export default function IssueAttachmentSection({ projectId, issueId, attachments }: Props) {
  const user = useAuthStore((s) => s.user);
  const uploadMutation = useUploadAttachments(projectId);
  const deleteMutation = useDeleteAttachment(projectId);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // 미디어 파일만 필터 (이미지 + 영상)
  const mediaItems = useMemo(
    () => attachments.filter((a) => a.mimeType.startsWith('image/') || a.mimeType.startsWith('video/')),
    [attachments],
  );

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;

      setUploadProgress(0);
      try {
        await uploadMutation.mutateAsync({
          issueId,
          files: fileArray,
          onProgress: (p) => setUploadProgress(p),
        });
        toast.success(`${fileArray.length}개 파일이 업로드되었습니다.`);
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || '파일 업로드에 실패했습니다.');
      } finally {
        setUploadProgress(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [issueId, uploadMutation],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleDelete = useCallback(
    async (attachment: IssueAttachment) => {
      const confirmed = window.confirm(`"${attachment.originalName}"을(를) 삭제하시겠습니까?`);
      if (!confirmed) return;

      try {
        await deleteMutation.mutateAsync({
          issueId,
          attachmentId: attachment.attachmentId,
        });
        toast.success('첨부파일이 삭제되었습니다.');
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || '삭제에 실패했습니다.');
      }
    },
    [issueId, deleteMutation],
  );

  const canDelete = (attachment: IssueAttachment) =>
    user?.id === attachment.uploadedBy || user?.role === 'SUPER_ADMIN';

  const handleAttachmentClick = (attachment: IssueAttachment) => {
    if (attachment.mimeType.startsWith('image/') || attachment.mimeType.startsWith('video/')) {
      const idx = mediaItems.findIndex((m) => m.attachmentId === attachment.attachmentId);
      if (idx >= 0) setViewerIndex(idx);
    } else {
      const a = document.createElement('a');
      a.href = attachment.url;
      a.download = attachment.originalName;
      a.click();
    }
  };

  const goNext = useCallback(() => {
    if (viewerIndex === null) return;
    setViewerIndex((viewerIndex + 1) % mediaItems.length);
  }, [viewerIndex, mediaItems.length]);

  const goPrev = useCallback(() => {
    if (viewerIndex === null) return;
    setViewerIndex((viewerIndex - 1 + mediaItems.length) % mediaItems.length);
  }, [viewerIndex, mediaItems.length]);

  // 키보드 네비게이션
  useEffect(() => {
    if (viewerIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setViewerIndex(null);
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [viewerIndex, goNext, goPrev]);

  return (
    <div className="px-5 py-3 border-t border-gray-100">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
        <Paperclip className="inline size-3.5 mr-1" />
        첨부파일 ({attachments.length})
      </h3>

      {/* 첨부파일 목록 */}
      {attachments.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {attachments.map((att) => (
            <div
              key={att.attachmentId}
              className="relative group border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:border-primary-300 transition-colors"
              onClick={() => handleAttachmentClick(att)}
            >
              {/* 썸네일 또는 아이콘 */}
              {att.mimeType.startsWith('image/') && !failedImages.has(att.attachmentId) ? (
                <div className="aspect-square bg-gray-50">
                  <img
                    src={att.url}
                    alt={att.originalName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={() => setFailedImages((prev) => new Set(prev).add(att.attachmentId))}
                  />
                </div>
              ) : att.mimeType.startsWith('image/') && failedImages.has(att.attachmentId) ? (
                <div className="aspect-square bg-gray-50 flex items-center justify-center">
                  <Image className="size-8 text-gray-300" />
                </div>
              ) : att.mimeType.startsWith('video/') ? (
                <div className="aspect-square bg-gray-900 flex items-center justify-center">
                  <Film className="size-8 text-white/70" />
                </div>
              ) : (
                <div className="aspect-square bg-gray-50 flex items-center justify-center">
                  {getFileIcon(att.mimeType)}
                </div>
              )}

              {/* 파일 정보 */}
              <div className="px-2 py-1.5">
                <div className="text-sm text-gray-700 truncate" title={att.originalName}>
                  {att.originalName}
                </div>
                <div className="text-[11px] text-gray-400">{formatFileSize(att.fileSize)}</div>
              </div>

              {/* 삭제 버튼 */}
              {canDelete(att) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(att);
                  }}
                  className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  title="삭제"
                >
                  <X className="size-3" />
                </button>
              )}

              {/* 다운로드 아이콘 (비이미지/비영상) */}
              {!att.mimeType.startsWith('image/') && !att.mimeType.startsWith('video/') && (
                <div className="absolute top-1 left-1 p-1 bg-black/30 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <Download className="size-3" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 업로드 영역 */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
        }`}
      >
        {uploadProgress !== null ? (
          <div className="space-y-2">
            <Spinner className="size-5 mx-auto" />
            <div className="text-sm text-gray-500">업로드 중... {uploadProgress}%</div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-primary-600 h-1.5 rounded-full transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <Upload className="size-5 text-gray-400 mx-auto mb-1" />
            <div className="text-sm text-gray-500">파일을 드래그하거나 클릭하여 첨부</div>
            <div className="text-[11px] text-gray-400 mt-1">
              이미지(PNG, JPG, GIF, 10MB) | 영상(MP4, WebM, 50MB) | 문서(PDF, 20MB)
            </div>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,.pdf,.zip"
        hidden
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
        }}
      />

      {/* 미디어 뷰어 (이미지 + 영상 통합) */}
      {viewerIndex !== null && mediaItems[viewerIndex] && (() => {
        const current = mediaItems[viewerIndex];
        const isImage = current.mimeType.startsWith('image/');
        return (
          <div
            className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center"
            onClick={() => setViewerIndex(null)}
          >
            {/* 닫기 */}
            <button
              onClick={() => setViewerIndex(null)}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/40 rounded-full transition-colors z-10"
            >
              <X className="size-6" />
            </button>

            {/* 파일명 + 카운터 */}
            <div className="absolute top-4 left-4 text-white/80 text-sm bg-black/40 px-3 py-1.5 rounded-full z-10">
              {current.originalName} ({viewerIndex + 1}/{mediaItems.length})
            </div>

            {/* 이전 버튼 */}
            {mediaItems.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-colors z-10"
              >
                <ChevronLeft className="size-6" />
              </button>
            )}

            {/* 미디어 콘텐츠 */}
            {isImage ? (
              <img
                key={current.attachmentId}
                src={current.url}
                alt={current.originalName}
                className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <video
                key={current.attachmentId}
                controls
                autoPlay
                src={current.url}
                className="max-w-[90vw] max-h-[85vh] rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* 다음 버튼 */}
            {mediaItems.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-colors z-10"
              >
                <ChevronRight className="size-6" />
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
}
